import React from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { MolecularBg } from "../components/MolecularBg";
import { useSEO } from "../lib/useSEO";

const CONTACT_EMAIL = "carbonlabsau@outlook.com";
const SITE_DOMAIN = "carbonlabsau.com";

const TERMS_SECTIONS = [
    {
        heading: "1. Acceptance of Terms",
        body: [
            `By accessing or using the Carbon Labs website (the "Site") or purchasing any product from us, you confirm that you are at least 18 years of age, that you have read and understood these Terms of Service ("Terms"), and that you agree to be bound by them. If you do not agree to these Terms, you must not use the Site or purchase any products.`,
        ],
    },
    {
        heading: "2. Research Use Only",
        body: [
            "All peptides and compounds listed on this Site are sold strictly for legitimate in-vitro scientific research conducted by qualified researchers in professional laboratory settings. By purchasing any product, you represent and warrant that:",
        ],
        list: [
            "You are a qualified researcher, scientist, or institution purchasing for lawful laboratory research purposes only.",
            "You will not use any purchased compound for human consumption, veterinary use, or any purpose other than in-vitro research.",
            "You understand that the compounds have not been evaluated or approved by the Therapeutic Goods Administration (TGA) or any equivalent regulatory authority for therapeutic use.",
            "You will comply with all applicable local, state, and federal laws and regulations governing the purchase, handling, storage, and use of research chemicals.",
        ],
    },
    {
        heading: "3. Eligibility, Minors & Capacity",
        body: [
            "You must be at least 18 years of age, have legal capacity to enter into a binding contract, and be authorised to use the nominated payment method in order to place an order with Carbon Labs. By using the Site or placing an order, you represent and warrant that you satisfy each of those requirements.",
            "If you misrepresent your age, legal capacity, identity, or payment authority in connection with an order, that conduct constitutes a material breach of these Terms. In those circumstances, we may refuse service, cancel the order, decline future orders, report the matter where appropriate, and take any other action reasonably necessary to protect our business, payment providers, and customers.",
        ],
    },
    {
        heading: "4. Product Information & Accuracy",
        body: [
            "We make every reasonable effort to ensure that product descriptions, purity data, and specifications displayed on the Site are accurate. A manufacturer-supplied Certificate of Analysis (COA) is available for each product. The COA is provided by the manufacturer, represents the applicable manufacturing batch, and should not be interpreted as being unique to each individual vial. However, we do not warrant that product descriptions or other content on the Site are error-free, complete, or current. We reserve the right to correct errors, inaccuracies, or omissions and to change or update information at any time without prior notice.",
        ],
    },
    {
        heading: "5. Ordering & Payment",
        body: [
            "By submitting an order through the Site, you are making an offer to purchase the selected products at the stated price. All orders are subject to acceptance and availability. We reserve the right to refuse or cancel any order at our sole discretion, including orders that appear to be placed for non-research or fraudulent purposes.",
            "Prices are listed in Australian Dollars (AUD) and are inclusive of applicable GST unless otherwise stated. Payment is processed securely through Stripe and supports cards (Visa, Mastercard, Amex), Apple Pay, Google Pay, Link, Afterpay, and Zip. Payment must be completed in full before any order is dispatched. We do not store full card details.",
        ],
    },
    {
        heading: "6. Shipping & Delivery",
        body: [
            "We endeavour to dispatch all in-stock orders placed before 2:00 pm AEST/AEDT within the same business day of payment confirmation. Orders placed after the cut-off are dispatched the next business day. Shipping times are estimates only and are not guaranteed. Risk of loss and title for products pass to you upon delivery to the carrier. We are not responsible for delays caused by customs, carrier issues, or circumstances beyond our control. It is your responsibility to ensure that importing the relevant compounds is lawful in your jurisdiction.",
            "Where a product is sold on a pre-order basis, any dispatch date, ETA window, or estimated stock arrival date is an estimate only and is not guaranteed. Pre-order timelines may change due to supplier, manufacturing, import, customs, freight, or carrier delays and other circumstances outside our reasonable control.",
        ],
    },
    {
        heading: "7. Returns & Refunds",
        body: [
            "Due to the nature of research compounds and the requirement for cold-chain integrity, we do not accept returns of opened or used products. If you receive a product that is damaged, incorrect, or defective, please contact us within 7 days of receipt with supporting evidence (e.g., photographs). We will assess each case on its merits and, at our discretion, offer a replacement or refund.",
            "If an order is placed through fraudulent or false misrepresentation of age, legal capacity, identity, or authority to use a payment method, and the order has already been dispatched, no refund will be provided for dispatched consumable, health, or research goods that cannot safely be returned or resold due to their nature, to the extent permitted by law.",
            "Refunds are not automatically available solely because a pre-order dispatch or stock arrival estimate is delayed. Any request for a refund arising from a pre-order delay will be assessed by us on a case-by-case basis. We may approve a refund where a delay becomes unreasonably long or where we otherwise consider a refund appropriate in the circumstances, but any such refund remains at our discretion except where a remedy is otherwise required by law.",
            "Nothing in these Terms excludes, restricts, or modifies any rights or remedies you may have under the Australian Consumer Law or any other law where those rights cannot lawfully be excluded, restricted, or modified.",
        ],
    },
    {
        heading: "8. Disclaimer of Warranties",
        body: [
            `To the maximum extent permitted by law, the Site and all products are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Site will be uninterrupted, error-free, or free of viruses or other harmful components.`,
        ],
    },
    {
        heading: "9. Limitation of Liability",
        body: [
            "To the fullest extent permitted by applicable law, Carbon Labs and its directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to your use of the Site or products, including but not limited to personal injury, property damage, lost profits, or data loss — even if we have been advised of the possibility of such damages. Our total liability to you for any claim shall not exceed the amount paid by you for the specific product giving rise to the claim.",
        ],
    },
    {
        heading: "10. Intellectual Property",
        body: [
            "All content on the Site, including text, graphics, logos, the CARBON LABS name and mark, product renders, and supporting documentation, is the property of Carbon Labs and is protected by applicable copyright and intellectual property laws. You may not reproduce, distribute, or create derivative works from any Site content without our prior written permission.",
        ],
    },
    {
        heading: "11. Privacy",
        body: [
            "Your use of the Site is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Site, you consent to the collection and use of your information as described in our Privacy Policy.",
        ],
    },
    {
        heading: "12. Governing Law",
        body: [
            "These Terms are governed by and construed in accordance with the laws of New South Wales, Australia. You agree to submit to the exclusive jurisdiction of the courts located in Australia for the resolution of any disputes arising from these Terms or your use of the Site.",
        ],
    },
    {
        heading: "13. Changes to These Terms",
        body: [
            `We reserve the right to update or modify these Terms at any time. The updated Terms will be posted on this page with a revised "Last updated" date. Your continued use of the Site following any changes constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.`,
        ],
    },
    {
        heading: "14. Contact Us",
        body: [
            "If you have any questions about these Terms of Service, please contact us:",
            "Carbon Labs · Sydney, NSW, Australia",
            "Email: carbonlabsau@outlook.com",
        ],
        contact: true,
    },
];

const CONTENT = {
    shipping: {
        title: "Shipping Policy",
        intro: null,
        sections: [
            {
                heading: "Dispatch",
                body: [
                    "All in-stock orders placed before 2:00 pm AEST/AEDT on a business day are dispatched the same business day. Orders placed after the cut-off ship the next business day.",
                    "All shipments leave from Sydney via Australia Post in secure, discreet, temperature-controlled packaging. No branding is shown on the outer parcel.",
                ],
            },
            {
                heading: "Delivery Times",
                body: [
                    "Standard domestic delivery typically takes 2–5 business days depending on your location. Express options are available at checkout.",
                    "AusPost's estimated delivery dates are estimates only — parcels frequently arrive sooner than the displayed window. State-based public holidays may extend an estimate by a day or two.",
                ],
            },
            {
                heading: "Tracking",
                body: [
                    "Once dispatched you'll receive an email with your Australia Post tracking number.",
                ],
            },
            {
                heading: "International Shipping",
                body: [
                    "We currently ship within Australia only. We do not offer international shipping at this time.",
                ],
            },
        ],
    },
    returns: {
        title: "Returns Policy",
        intro: null,
        sections: [
            {
                heading: "General",
                body: [
                    "Due to the temperature-sensitive nature of research compounds and the cold-chain integrity requirement, we do not accept returns of opened or used products.",
                ],
            },
            {
                heading: "Damaged or Incorrect Items",
                body: [
                    "If your order arrives damaged, incorrect, or defective, photograph the packaging and product immediately upon receipt and email us at " + CONTACT_EMAIL + " within 7 days of delivery.",
                    "We will assess each case on its merits and, at our discretion, arrange a replacement or refund.",
                ],
            },
            {
                heading: "Change of Mind",
                body: [
                    "We do not accept returns for change of mind or research outcome.",
                ],
            },
        ],
    },
    privacy: {
        title: "Privacy Policy",
        intro: null,
        sections: [
            {
                heading: "What We Collect",
                body: [
                    "We collect only the information necessary to fulfil your order and provide customer support: name, email, shipping address, and payment information (processed by Stripe — we do not store full card details).",
                ],
            },
            {
                heading: "How We Use It",
                body: [
                    "Your information is used solely to process and fulfil orders, send order-related emails (confirmations, dispatch notifications, post-purchase review requests), and respond to support enquiries.",
                    "We do not sell or share your personal data with third parties for marketing.",
                ],
            },
            {
                heading: "Email & Cookies",
                body: [
                    "Newsletter subscriptions can be cancelled at any time via the unsubscribe link in each email.",
                    "Cookies are used solely to maintain your shopping cart session.",
                ],
            },
            {
                heading: "Contact",
                body: [
                    "For any privacy questions, contact us at " + CONTACT_EMAIL + ".",
                ],
            },
        ],
    },
    terms: {
        title: "Terms of Service",
        lastUpdated: "16 February 2026",
        intro: (
            <>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3">
                    <strong className="text-white">Important Notice:</strong> All products sold by Carbon Labs are
                    intended exclusively for in-vitro laboratory research purposes. They are not approved for human
                    or veterinary consumption. By accessing this website or placing an order, you agree to these
                    Terms of Service in full.
                </p>
                <p className="text-xs font-mono text-[var(--text-muted)] mb-6">
                    Issued by Carbon Labs · Sydney, NSW, Australia
                </p>
            </>
        ),
        sections: TERMS_SECTIONS,
    },
};

const SEO_BY_SLUG = {
    terms: {
        title: "Terms of Service — Research Peptides AU · Carbon Labs",
        description: "Carbon Labs Terms of Service. Research use only. By placing an order you agree to these legally binding terms governing purchases, shipping, returns, and compliance in Australia.",
    },
    shipping: {
        title: "Shipping Policy — Same-Day Sydney Dispatch · Carbon Labs AU",
        description: "Carbon Labs shipping policy. Same-day dispatch from Sydney before 2 PM AEST/AEDT. Discreet temperature-controlled packaging via Australia Post.",
    },
    returns: {
        title: "Returns Policy — Damaged & Incorrect Items · Carbon Labs AU",
        description: "Carbon Labs returns policy for damaged or incorrect research peptide orders. Cold-chain integrity rules apply. Report issues within 7 days.",
    },
    privacy: {
        title: "Privacy Policy — How We Handle Your Data · Carbon Labs AU",
        description: "Carbon Labs privacy policy. We collect only the data needed to fulfil your order. Stripe-secured payments. We do not share your data with external marketing partners.",
    },
};

export const PolicyPage = () => {
    const { slug } = useParams();
    const content = CONTENT[slug] || { title: "Policy", sections: [{ heading: "", body: ["Not found."] }] };
    const isTerms = slug === "terms";
    useSEO(SEO_BY_SLUG[slug] || { title: `${content.title} · Carbon Labs AU`, description: `${content.title} for Carbon Labs research peptides.` });

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="policy-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10 max-w-3xl">
                <span className="eyebrow">// Legal</span>
                <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 mb-2">
                    {content.title}
                </h1>
                {content.lastUpdated && (
                    <p className="text-xs text-[var(--text-secondary)] mb-8 uppercase tracking-wider">
                        Last updated: {content.lastUpdated}
                    </p>
                )}

                {content.intro}

                <div className="glass-elevated rounded-2xl p-6 md:p-8 space-y-8">
                    {content.sections.map((s, i) => (
                        <section key={i} data-testid={`policy-section-${i}`}>
                            {s.heading && (
                                <h2 className="font-display text-lg font-bold text-[var(--gold)] mb-3 uppercase tracking-wider">
                                    {s.heading}
                                </h2>
                            )}
                            {s.body.map((p, j) => (
                                <p key={j} className="text-[var(--text-secondary)] leading-relaxed mb-3 last:mb-0">
                                    {p}
                                </p>
                            ))}
                            {s.list && (
                                <ul className="list-disc list-outside ml-5 mt-3 space-y-2">
                                    {s.list.map((l, k) => (
                                        <li key={k} className="text-[var(--text-secondary)] leading-relaxed">
                                            {l}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {s.contact && (
                                <div className="mt-3 space-y-1 text-[var(--text-secondary)]">
                                    <p className="text-white font-semibold">Carbon Labs</p>
                                    <p>Australia</p>
                                    <p>
                                        Email:{" "}
                                        <a
                                            className="text-[var(--gold)] underline"
                                            href={`mailto:${CONTACT_EMAIL}`}
                                        >
                                            {CONTACT_EMAIL}
                                        </a>
                                    </p>
                                    <p>
                                        Web:{" "}
                                        <a className="text-[var(--gold)] underline" href={`https://www.${SITE_DOMAIN}`}>
                                            www.{SITE_DOMAIN}
                                        </a>
                                    </p>
                                </div>
                            )}
                        </section>
                    ))}
                </div>

                {isTerms && (
                    <div
                        className="mt-10 rounded-2xl p-6 md:p-8 border border-[var(--gold)]/30 bg-gradient-to-br from-[rgba(233,188,11,0.06)] to-[rgba(233,188,11,0.02)]"
                        data-testid="research-use-only-callout"
                    >
                        <div className="flex items-start gap-4">
                            <div className="shrink-0 w-10 h-10 rounded-full bg-[var(--gold)]/15 flex items-center justify-center">
                                <AlertTriangle className="text-[var(--gold)]" size={20} />
                            </div>
                            <div>
                                <h3 className="font-display text-lg font-bold text-[var(--gold)] mb-2 uppercase tracking-wide">
                                    Important: Research Use Only
                                </h3>
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                                    All products sold by Carbon Labs are intended strictly for in-vitro research
                                    and laboratory use only. They are not approved for human or veterinary
                                    consumption and are not classified as medicines, supplements, or food
                                    products. By purchasing from us, you confirm that products will be used
                                    solely for lawful research purposes. We fully support the regulatory
                                    framework governing these compounds in Australia and these statements have
                                    not been evaluated by the TGA.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
